import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { t } from "@/i18n";
import { Feed, FULL_H } from "@/components/Feed";
import { FeedCard } from "@/components/FeedCard";
import { Spinner, Empty, BackButton } from "@/components/Common";

export function Scopri() {
  const [mode, setMode] = useState<"per_te" | "seguiti">("per_te");
  const params = useMemo(() => ({ mode }), [mode]);
  return (
    <div className="relative" data-testid="scopri-page">
      <div className="absolute top-0 md:top-3 inset-x-0 md:right-3 z-30 flex items-center justify-center md:justify-start px-6 py-4 bg-gradient-to-b from-black/60 to-transparent md:rounded-t-[1.75rem] pointer-events-none">
        <div className="flex gap-5 pointer-events-auto text-white">
          {(["per_te", "seguiti"] as const).map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`feed-mode-${m}`} className={`min-h-[44px] font-semibold border-b-2 ${mode === m ? "border-white text-white" : "border-transparent text-white/60"}`}>{t(`feed.${m}` as any)}</button>
          ))}
        </div>
      </div>
      <Feed params={params} />
    </div>
  );
}

export function FilteredFeed({ kind }: { kind: "category" | "hashtag" }) {
  const { slug = "" } = useParams();
  const { data: cat } = useQuery({ queryKey: ["cat", slug], queryFn: () => api.get(`/categories/${slug}`).then((r) => r.data), enabled: kind === "category" });
  const title = kind === "category" ? cat?.name || slug : `#${slug}`;
  const params = useMemo(() => (kind === "category" ? { category: slug, mode: "per_te" } : { hashtag: slug, mode: "per_te" }), [kind, slug]);
  return (
    <div className="relative" data-testid={`${kind}-page`}>
      <div className="absolute top-0 md:top-3 inset-x-0 md:right-3 z-30 flex items-center gap-3 px-4 py-4 bg-gradient-to-b from-black/60 to-transparent text-white md:rounded-t-[1.75rem]">
        <BackButton parent="/cerca" />
        <div>
          <div className="label text-white/70">{kind === "category" ? "Categoria" : "Hashtag"}</div>
          <div className="font-display font-bold text-lg" data-testid="filtered-feed-title">{title}</div>
        </div>
      </div>
      <Feed params={params} />
    </div>
  );
}

export function ContentPage() {
  const { id } = useParams();
  const [muted, setMuted] = useState(true);
  const { data, isLoading, error } = useQuery({ queryKey: ["content", id], queryFn: () => api.get(`/contents/${id}`).then((r) => r.data), retry: false });
  return (
    <div className={`relative ${FULL_H} md:h-[calc(100dvh-1.5rem)] md:my-3 md:mr-3 md:rounded-[1.75rem] overflow-hidden bg-[hsl(168_20%_12%)] text-white`} data-testid="content-page">
      <div className="absolute top-4 left-4 z-30"><BackButton parent="/feed" /></div>
      {isLoading ? <div className="p-10"><Spinner /></div> : error || !data ? <div className="p-10 pt-16"><Empty testId="content-not-found">Contenuto non disponibile.</Empty></div> :
        data.status === "removed" ? <div className="p-10 pt-16"><Empty testId="content-removed">Contenuto rimosso dalla moderazione. Motivazione: {data.removed_reason}</Empty></div> :
        <FeedCard item={data} muted={muted} onMute={() => setMuted((m) => !m)} />}
    </div>
  );
}
